import { Link, useLocation, useNavigate } from "react-router-dom";
import { Zap, Menu, X, LayoutDashboard, LogOut, ChevronDown, PackageSearch, Calculator, Phone, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth, ADMIN_ROLES } from "../context/AuthContext";
import api from "../lib/api";

export const Brand = ({ dark, testid = "nav-brand-logo" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    api.get("/shop/public/site-logo")
      .then((r) => {
        if (r.data?.has_logo) setLogo(`${api.defaults.baseURL}/shop/public/site-logo.png?v=${r.data.updated_at || ""}`);
      })
      .catch(() => {});
  }, []);
  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  return (
    <a href="/" onClick={goHome} data-testid={testid} className="flex items-center gap-2.5">
      {logo ? (
        <img src={logo} alt="INDIAFAST" className="h-9 w-auto max-w-[170px] object-contain" data-testid="nav-brand-custom-logo" />
      ) : (
        <>
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-saffron text-white shadow-lg shadow-saffron/30">
            <Zap className="h-5 w-5" fill="currentColor" />
          </span>
          <span className={`font-heading text-xl font-extrabold tracking-tight ${dark ? "text-white" : "text-navy"}`}>
            INDIA<span className="text-saffron">FAST</span>
          </span>
        </>
      )}
    </a>
  );
};

const COURIER_ITEMS = [
  { label: "Domestic Courier", href: "/domestic-courier", testid: "nav-courier-domestic" },
  { label: "International Courier", href: "/international-courier", testid: "nav-courier-international" },
  { label: "eCommerce Shipping", href: "/ecommerce-shipping", testid: "nav-courier-ecommerce" },
  { label: "Business Courier", href: "/business-courier", testid: "nav-courier-business" },
  { label: "NRI Courier", href: "/nri-courier", testid: "nav-courier-nri" },
];

const COMPANY_ITEMS = [
  { label: "About Us", href: "/p/about", testid: "nav-company-about" },
  { label: "Branches", href: "/branches", testid: "nav-company-branches" },
  { label: "Shop", href: "/shop", testid: "nav-company-shop" },
  { label: "Media & Videos", href: "/media-videos", testid: "nav-company-media" },
  { label: "News & Insights", href: "/p/news", testid: "nav-company-news" },
  { label: "Careers", href: "/p/careers", testid: "nav-company-careers" },
  { label: "Partner With Us", href: "/partner-with-us", testid: "nav-company-partner" },
];

const SUPPORT_ITEMS = [
  { label: "Track Shipment", href: "/track", testid: "nav-support-track" },
  { label: "Pickup Request", href: "/pickup-request", testid: "nav-support-pickup" },
  { label: "FAQ / Help Center", href: "/p/faq", testid: "nav-support-faq" },
  { label: "Complaint", href: "/complaint", testid: "nav-support-complaint" },
  { label: "Call +91 97838 07838", href: "tel:9783807838", testid: "nav-support-call" },
];

const TOP_LINKS = [
  { label: "Destinations", href: "/#destinations", testid: "nav-link-destinations" },
  { label: "Rate Calculator", href: "/rate-calculator", testid: "nav-link-calculator" },
  { label: "Track", href: "/track", testid: "nav-link-track" },
];

export const PillArrow = () => (
  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
    <ArrowRight className="h-3 w-3" />
  </span>
);

function Dropdown({ label, items, goTo, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={testid}
              className="flex items-center gap-1 text-sm font-medium text-slate-600 transition-colors hover:text-saffron">
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full w-56 pt-2" data-testid={`${testid}-dropdown`}>
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl">
            {items.map((item) => (
              <a key={item.label} href={item.href} onClick={(e) => goTo(e, item)} data-testid={item.testid}
                 className="block px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-saffron-light hover:text-saffron">
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileActionBar({ hidden }) {
  if (hidden) return null;
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-white/95 px-3 py-2 backdrop-blur-xl lg:hidden"
         data-testid="mobile-action-bar" aria-label="Quick actions">
      <div className="grid grid-cols-3 gap-2">
        <Link to="/track" data-testid="mobile-bar-track"
              className="flex items-center justify-center gap-1.5 rounded-xl bg-navy py-2.5 text-xs font-bold text-white">
          <PackageSearch className="h-4 w-4" /> Track
        </Link>
        <Link to="/rate-calculator" data-testid="mobile-bar-quote"
              className="flex items-center justify-center gap-1.5 rounded-xl bg-saffron py-2.5 text-xs font-bold text-white">
          <Calculator className="h-4 w-4" /> Get Quote
        </Link>
        <a href="tel:9783807838" data-testid="mobile-bar-call"
           className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2.5 text-xs font-bold text-white">
          <Phone className="h-4 w-4" /> Call
        </a>
      </div>
    </nav>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dashPath = !user ? "/app" : user.role === "ccare" ? "/ccare" : ADMIN_ROLES.includes(user.role) ? "/admin" : "/app";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (e, l) => {
    if (l.href.startsWith("tel:")) { setOpen(false); return; }
    e.preventDefault();
    setOpen(false);
    if (l.href.startsWith("/#")) {
      const id = l.href.slice(2);
      const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(scroll, 150);
      } else {
        scroll();
      }
    } else {
      navigate(l.href);
    }
  };

  const renderLink = (l, mobile = false) => (
    <a key={l.label} href={l.href} onClick={(e) => goTo(e, l)}
       data-testid={l.testid || `nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
       className={mobile
         ? "block py-2.5 text-sm font-medium text-slate-700"
         : "text-sm font-medium text-slate-600 transition-colors hover:text-saffron"}>
      {l.label}
    </a>
  );

  const renderMobileGroup = (title, items) => (
    <div key={title}>
      <p className="pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
      {items.map((item) => (
        <a key={item.label} href={item.href} onClick={(e) => goTo(e, item)} data-testid={`mobile-${item.testid}`}
           className="block py-2.5 pl-3 text-sm font-medium text-slate-700">
          {item.label}
        </a>
      ))}
    </div>
  );

  const isInternal = /^\/(app|admin|crm|pickup)(\/|$)/.test(location.pathname);

  return (
    <>
      <header className={`glass-header fixed inset-x-0 top-0 z-50 transition-shadow duration-300 ${scrolled ? "shadow-lg shadow-navy/10" : ""}`}
              data-testid="main-navbar">
        <div className="tricolor-beam h-[3px] w-full" />
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Brand />
          <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
            <Dropdown label="Courier" items={COURIER_ITEMS} goTo={goTo} testid="nav-courier-menu" />
            {TOP_LINKS.map((l) => renderLink(l))}
            <Dropdown label="Company" items={COMPANY_ITEMS} goTo={goTo} testid="nav-company-menu" />
            <Dropdown label="Support" items={SUPPORT_ITEMS} goTo={goTo} testid="nav-support-menu" />
          </nav>
          <div className="hidden items-center gap-2.5 lg:flex">
            {user ? (
              <>
                <Link to={dashPath} data-testid="nav-link-dashboard"
                      className="flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white">
                  <LayoutDashboard className="h-4 w-4" /> Dashboard
                </Link>
                <button onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout-btn"
                        className="rounded-full p-2 text-slate-500 hover:bg-stone-100" aria-label="Logout">
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-link-login" className="px-1.5 text-sm font-semibold text-navy hover:text-saffron">Login</Link>
                <Link to="/track" data-testid="nav-pill-track"
                      className="group hidden items-center gap-2 rounded-full border border-stone-300 bg-white py-1.5 pl-4 pr-1.5 text-xs font-bold text-navy transition-colors hover:border-saffron hover:text-saffron xl:inline-flex">
                  Track Shipment <PillArrow />
                </Link>
                <Link to="/rate-calculator" data-testid="nav-pill-quote"
                      className="group hidden items-center gap-2 rounded-full border border-stone-300 bg-white py-1.5 pl-4 pr-1.5 text-xs font-bold text-navy transition-colors hover:border-saffron hover:text-saffron xl:inline-flex">
                  Get a Quote <PillArrow />
                </Link>
                <Link to="/register" data-testid="nav-ship-now-btn"
                      className="group inline-flex items-center gap-2 rounded-full bg-saffron py-1.5 pl-4 pr-1.5 text-xs font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover">
                  Ship Now
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              </>
            )}
          </div>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-btn"
                  aria-label="Menu" aria-expanded={open}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="animate-fade-up max-h-[80vh] overflow-y-auto border-t border-stone-200 bg-white px-4 py-4 lg:hidden"
               data-testid="nav-mobile-menu">
            {renderMobileGroup("Courier", COURIER_ITEMS)}
            <div className="pt-1">{TOP_LINKS.map((l) => renderLink(l, true))}</div>
            {renderMobileGroup("Company", COMPANY_ITEMS)}
            {renderMobileGroup("Support", SUPPORT_ITEMS)}
            <div className="mt-3 flex gap-3">
              {user ? (
                <Link to={dashPath} data-testid="mobile-nav-dashboard"
                      className="flex-1 rounded-full bg-navy px-4 py-2.5 text-center text-sm font-bold text-white">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" data-testid="mobile-nav-login"
                        className="flex-1 rounded-full border border-stone-300 px-4 py-2.5 text-center text-sm font-semibold">Login</Link>
                  <Link to="/register" data-testid="mobile-nav-ship-now"
                        className="flex-1 rounded-full bg-saffron px-4 py-2.5 text-center text-sm font-bold text-white">Ship Now</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>
      <MobileActionBar hidden={open || isInternal} />
    </>
  );
}
